import React, { useRef } from 'react';
import { Button, DragAndDrop, IconSpinner, Modal } from '../../../../components';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import { useDispatch } from 'react-redux';
import { Field, Form as FinalForm } from 'react-final-form';
import css from './CreatePostForm.module.css';
import { handleFileUpload } from '../../../../util/circleHelper';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';

const dragAndDropHandler = async (
  file,
  form,
  values,
  dispatch,
  onDocumentClick,
  formOldValues,
  setFormOldValues
) => {
  try {
    const documentUrl = await handleFileUpload(file, dispatch);

    if (!documentUrl || !documentUrl.signed_id) {
      throw new Error('Invalid upload response');
    }

    // Handle both cases: with setFormOldValues (create post) and without (comments/replies)
    if (setFormOldValues && typeof setFormOldValues === 'function') {
      setFormOldValues(prev => ({
        ...prev,
        document: [...(prev?.document || []), documentUrl],
        documentsfiles: [
          ...(prev?.documentsfiles || []),
          { file, signed_id: documentUrl.signed_id },
        ],
      }));
    }

    if (form && form.change) {
      const currentValues = form.getState?.().values || values || {};
      form.change('document', [...(currentValues.document || []), documentUrl]);
      form.change('documentsfiles', [
        ...(currentValues.documentsfiles || []),
        { file, signed_id: documentUrl.signed_id },
      ]);
    }

    onDocumentClick();
  } catch (error) {
    console.error('Document upload failed:', error);
    // Don't throw - just log and close modal
    onDocumentClick();
  }
};

const DocumentFieldComponent = props => {
  const { form, values, inputRef, onDocumentClick, formOldValues, setFormOldValues } = props;
  const dispatch = useDispatch();
  return (
    <Field
      name="document"
      component={() => {
        const onChange = async e => {
          try {
            const file = e.target.files[0];
            if (!file) return;

            const documentUrl = await handleFileUpload(file, dispatch);

            if (!documentUrl || !documentUrl.signed_id) {
              throw new Error('Invalid upload response');
            }

            // Handle both cases: with setFormOldValues (create post) and without (comments/replies)
            if (setFormOldValues && typeof setFormOldValues === 'function') {
              setFormOldValues(prev => ({
                ...prev,
                document: [...(prev?.document || []), documentUrl],
                documentsfiles: [
                  ...(prev?.documentsfiles || []),
                  { file, signed_id: documentUrl.signed_id },
                ],
              }));
            }

            if (form && form.change) {
              const currentValues = form.getState?.().values || values || {};
              form.change('document', [...(currentValues.document || []), documentUrl]);
              form.change('documentsfiles', [
                ...(currentValues.documentsfiles || []),
                { file, signed_id: documentUrl.signed_id },
              ]);
            }

            onDocumentClick();
          } catch (error) {
            console.error('Document upload failed:', error);
            // Don't throw - just log and close modal
            onDocumentClick();
          }
        };
        const inputProps = {
          accept:
            'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          onChange,
          type: 'file',
        };
        return (
          <div style={{ display: 'none' }}>
            <input ref={inputRef} data-testid="document-file-input" {...inputProps} />
          </div>
        );
      }}
    />
  );
};

const FileUploadModal = props => {
  const {
    isDocumentModal,
    onDocumentClick,
    form,
    values,
    requestDirectUpload,
    formOldValues,
    setFormOldValues,
    isOnline = true,
    usePortal = false,
  } = props;
  if (!isOnline) {
    return null;
  }
  const dispatch = useDispatch();

  const inputRef = useRef(null);

  const handleOpenFileInput = () => {
    inputRef.current.click(); // Trigger file input on button click
  };

  return (
    <div>
      <Modal
        id={'fileUploadModal'}
        isOpen={isDocumentModal}
        onClose={() => {
          if (!requestDirectUpload) {
            onDocumentClick();
          }
        }}
        onManageDisableScrolling={manageDisableScrolling}
        className={css.uploadModals}
        usePortal={usePortal}
      >
        <div className={css.contentWrapper}>
          {requestDirectUpload ? (
            <div className={css.loader}>
              <IconSpinner type={true} />
            </div>
          ) : (
            <DragAndDrop
              handleDrop={files =>
                dragAndDropHandler(
                  files[0],
                  form,
                  values,
                  dispatch,
                  onDocumentClick,
                  formOldValues,
                  setFormOldValues
                )
              }
              acceptedFileType={'document'}
            >
              <div className={css.uploadWrapper}>
                <div className={css.headingName}>Add File</div>
                <div className={css.boxUploader}>
                  <div className={css.plusIcon}>
                    <span className={css.icon}>
                      <IconCard brand="upload" />
                    </span>
                  </div>
                  <div className={css.dragText}>Drop your file here to upload</div>
                  <div className={css.fileTypes}>Works with PDF, DOC, DOCX file</div>

                  <Button
                    onClick={handleOpenFileInput}
                    type="button"
                    className={css.chooseFileButton}
                  >
                    Choose file
                  </Button>
                  <DocumentFieldComponent
                    form={form}
                    values={values}
                    inputRef={inputRef}
                    onDocumentClick={onDocumentClick}
                    formOldValues={formOldValues}
                    setFormOldValues={setFormOldValues}
                  />
                </div>
              </div>
            </DragAndDrop>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default FileUploadModal;